import { TreegridGanttUnit } from "../../treegrid/types"
import { api } from "../../api"
import { BoardViewModel, makeBoardViewModel } from "../../boards/api/board"
import { ProjectViewModel, makeProjectViewModel } from "../../projects/api/project"
import { ProjectResponse } from "../../projects/api/project-response"
import { GanttDependencyType, Status } from "../../types/common"
import { makeUrlSearchParams } from "../../utils/url"
import { TaskPermissionRecord } from "./task-permission-response"
import { OptionType, TaskResponse } from "../api/task-response"
import { TaskStateType } from "../types"
import { makeTaskApiEndpoints } from "./endpoints"
import { TaskViewModel, makeTaskViewModel } from "./task"
import { hasPendingMutations } from "../../utils/rtk-query-util"
import { DateTimeService } from "../../services/date-time-service"

const apiEndpoints = makeTaskApiEndpoints()
const pendingEndpointNames = [
  "addTaskManagers",
  "removeTaskManagers",
  "addTaskParticipants",
  "removeTaskParticipants",
  "addTaskSuppliers",
  "removeTaskSuppliers",
  "addTaskWorkspaces",
  "removeTaskWorkspaces",
]
export const taskApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getTaskById: builder.query<TaskViewModel, string>({
      query: apiEndpoints.taskItem,
      transformResponse: ({ task }: { task: TaskResponse }) => makeTaskViewModel(task),
      providesTags: (result) => [{ type: "Task", id: result?.id }],
    }),
    getTasks: builder.query<TaskViewModel[], TasksQueryParams | void>({
      query: (params) => ({
        method: "GET",
        url: apiEndpoints.tasks(),
        params: params ? makeUrlSearchParams(params) : undefined,
      }),
      transformResponse: ({ tasks }: { tasks: TaskResponse[] }) => tasks.map(makeTaskViewModel),
      providesTags: (result = []) => ["Task", ...result?.map(({ id }) => ({ type: "Task", id } as const))],
    }),
    getTaskPermissions: builder.query<TaskPermissionRecord[], string>({
      query: apiEndpoints.taskPermissions,
      providesTags: (result, error, taskId) => [{ type: "TaskPermission", id: taskId }],
    }),
    createUserTask: builder.mutation<CreateTaskReturnData, { userId: string } & NewTaskData>({
      query: ({ userId, ...taskData }) => ({
        method: "POST",
        url: apiEndpoints.userTasks(userId),
        body: taskData,
      }),
      transformResponse: (responseData: CreateTaskResponseData) => {
        return {
          created: makeTaskViewModel(responseData.created),
          updated: {
            tasks: responseData.updated.tasks.map(makeTaskViewModel),
            project: makeProjectViewModel(responseData.updated.project),
          },
        }
      },
      invalidatesTags: (result) => {
        if (!result) return []
        return [
          "Task",
          ...result.updated.tasks.map(({ id }) => ({ type: "Task", id } as const)),
          { type: "Project", id: result.updated.project.id },
        ]
      },
    }),
    createOrgTask: builder.mutation<CreateTaskReturnData, { orgId: string } & NewTaskData>({
      query: ({ orgId, ...taskData }) => ({
        method: "POST",
        url: apiEndpoints.orgTasks(orgId),
        body: taskData,
      }),
      transformResponse: (responseData: CreateTaskResponseData) => {
        return {
          created: makeTaskViewModel(responseData.created),
          updated: {
            tasks: responseData.updated.tasks.map(makeTaskViewModel),
            project: makeProjectViewModel(responseData.updated.project),
          },
        }
      },
      invalidatesTags: (result) => {
        if (!result) return []
        return [
          "Task",
          ...result.updated.tasks.map(({ id }) => ({ type: "Task", id } as const)),
          { type: "Project", id: result.updated.project.id },
        ]
      },
    }),
    bulkCreateTasks: builder.mutation<BulkCreateTasksReturnData, NewBulkTaskData>({
      query: (taskData) => {
        return {
          method: "POST",
          url: apiEndpoints.bulkCreateTasks(),
          body: taskData,
        }
      },
      transformResponse: (responseData: BulkCreateTaskResponseData) => {
        return {
          created: responseData.created.map(makeTaskViewModel),
          updated: {
            tasks: responseData.updated.tasks.map(makeTaskViewModel),
            project: responseData.updated.project ? makeProjectViewModel(responseData.updated.project) : null,
          },
        }
      },
      invalidatesTags: (result) => {
        if (!result) return []
        return ["Task", { type: "Project", id: result.updated.project?.id }, "Chat"]
      },
    }),
    copyTask: builder.mutation<CopyTaskReturnData, CopyTaskData>({
      query: (taskData) => ({
        method: "POST",
        url: apiEndpoints.copyTask(),
        body: taskData,
      }),
      transformResponse: ({ project, tasks }: { project: ProjectResponse; tasks: TaskResponse[] }) => {
        return {
          project: makeProjectViewModel(project),
          tasks: tasks.map(makeTaskViewModel),
        }
      },
      invalidatesTags: (result) => {
        if (!result) return []
        return [
          "Task",
          ...result.tasks.map(({ id }) => ({ type: "Task", id } as const)),
          { type: "Project", id: result.project.id },
        ]
      },
    }),
    updateTaskInfo: builder.mutation<TaskViewModel, TaskInfoUpdateData & { taskId: string }>({
      query: ({ taskId, ...taskData }) => ({
        method: "PUT",
        url: apiEndpoints.taskItem(taskId),
        body: taskData,
      }),
      transformResponse: makeTaskViewModel,
      onQueryStarted: async ({ taskId, ...taskData }, { dispatch, queryFulfilled, getState }) => {
        const patchResult = dispatch(
          taskApi.util.updateQueryData("getTaskById", taskId, (draft) => {
            Object.assign(draft, taskData)
          })
        )
        try {
          await queryFulfilled
          const state = getState()
          if (!hasPendingMutations(state, { endpointNames: "updateTaskInfo" }))
            dispatch(taskApi.util.invalidateTags(["Task", { type: "Task", id: taskId }, "Chat"]))
        } catch {
          patchResult.undo()
        }
      },
    }),
    updateTaskStatusDescription: builder.mutation<TaskViewModel, TaskStatusDescriptionUpdateData & { taskId: string }>({
      query: ({ taskId, ...taskData }) => ({
        method: "PUT",
        url: apiEndpoints.taskStatusDescription(taskId),
        body: taskData,
      }),
      transformResponse: makeTaskViewModel,
      onQueryStarted: async ({ taskId, ...updateData }, { dispatch, queryFulfilled, getState }) => {
        const patchResult = dispatch(
          taskApi.util.updateQueryData("getTaskById", taskId, (draft) => {
            Object.assign(draft, updateData)
          })
        )
        try {
          await queryFulfilled
          const state = getState()
          if (!hasPendingMutations(state, { endpointNames: "updateTaskStatusDescription" }))
            dispatch(taskApi.util.invalidateTags(["Task", { type: "Task", id: taskId }]))
        } catch {
          patchResult.undo()
        }
      },
    }),
    updateTaskPlan: builder.mutation<UpdatePlanReturnData, TaskPlanUpdateData & { taskId: string }>({
      query: ({ taskId, ...taskData }) => ({
        method: "PUT",
        url: apiEndpoints.taskPlan(taskId),
        body: taskData,
      }),
      transformResponse: ({ project, tasks }: { project: ProjectResponse; tasks: TaskResponse[] }) => {
        return {
          project: makeProjectViewModel(project),
          tasks: tasks.map(makeTaskViewModel),
        }
      },
      onQueryStarted: async (
        { taskId, plannedStartDate, plannedEndDate, ...taskData },
        { dispatch, queryFulfilled, getState }
      ) => {
        if (plannedStartDate && plannedEndDate && new Date(plannedEndDate) < new Date(plannedStartDate)) {
          return
        }
        const patchResult = dispatch(
          taskApi.util.updateQueryData("getTaskById", taskId, (draft) => {
            const nextStart = plannedStartDate ?? draft.plannedStartDate
            const nextEnd = plannedEndDate ?? draft.plannedEndDate
            if (!DateTimeService.validateProjectDuration(nextStart, nextEnd)) {
              return
            }

            if (plannedStartDate !== undefined) {
              if (draft.plannedEndDate && new Date(plannedStartDate) > new Date(draft.plannedEndDate)) {
                draft.plannedStartDate = draft.plannedEndDate
              } else {
                draft.plannedStartDate = plannedStartDate
              }
            }
            if (plannedEndDate !== undefined) {
              if (draft.plannedStartDate && new Date(plannedEndDate) < new Date(draft.plannedStartDate)) {
                draft.plannedEndDate = draft.plannedStartDate
              } else {
                draft.plannedEndDate = plannedEndDate
              }
            }
          })
        )
        try {
          const { data } = await queryFulfilled
          const state = getState()
          if (
            !hasPendingMutations(state, {
              endpointNames: "updateTaskPlan",
            })
          ) {
            dispatch(
              taskApi.util.invalidateTags([
                "Task",
                ...data.tasks.map(({ id }) => ({ type: "Task" as const, id })),
                { type: "Project", id: data.project.id },
              ])
            )
          }
        } catch {
          patchResult.undo()
        }
      },
    }),
    updateTaskDuration: builder.mutation<UpdatePlanReturnData, TaskDurationUpdateData & { taskId: string }>({
      query: ({ taskId, ...taskData }) => ({
        method: "PUT",
        url: apiEndpoints.taskDuration(taskId),
        body: taskData,
      }),
      transformResponse: ({ project, tasks }: { project: ProjectResponse; tasks: TaskResponse[] }) => {
        return {
          project: makeProjectViewModel(project),
          tasks: tasks.map(makeTaskViewModel),
        }
      },
      invalidatesTags: (result) => {
        if (!result) return []
        return [
          "Task",
          ...result.tasks.map(({ id }) => ({ type: "Task", id } as const)),
          { type: "Project", id: result.project.id },
        ]
      },
    }),
    updateTaskDaysLeft: builder.mutation<UpdatePlanReturnData, TaskDaysLeftUpdateData & { taskId: string }>({
      query: ({ taskId, ...taskData }) => ({
        method: "PUT",
        url: apiEndpoints.taskDaysLeft(taskId),
        body: taskData,
      }),
      transformResponse: ({ project, tasks }: { project: ProjectResponse; tasks: TaskResponse[] }) => {
        return {
          project: makeProjectViewModel(project),
          tasks: tasks.map(makeTaskViewModel),
        }
      },
      invalidatesTags: (result) => {
        if (!result) return []
        return [
          "Task",
          ...result.tasks.map(({ id }) => ({ type: "Task", id } as const)),
          { type: "Project", id: result.project.id },
        ]
      },
    }),
    updateTaskStatus: builder.mutation<UpdateStatusReturnData, TaskStatusUpdateData & { taskId: string }>({
      query: ({ taskId, ...taskData }) => ({
        method: "PUT",
        url: apiEndpoints.taskStatus(taskId),
        body: taskData,
      }),
      transformResponse: ({ project, tasks, board }: UpdateStatusResponseData) => {
        return {
          project: makeProjectViewModel(project),
          tasks: tasks.map(makeTaskViewModel),
          board: board ? makeBoardViewModel(board) : null,
        }
      },
      onQueryStarted: async ({ taskId, ...taskData }, { dispatch, queryFulfilled, getState }) => {
        const optimisticUpdateData = {
          ...taskData,
          customStatus: taskData.customStatus || taskData.status,
        }
        const patchResult = dispatch(
          taskApi.util.updateQueryData("getTaskById", taskId, (draft) => {
            if (optimisticUpdateData.status) {
              draft.status = optimisticUpdateData.status
              draft.customStatus = optimisticUpdateData.customStatus || optimisticUpdateData.status
              draft.subStatusId = optimisticUpdateData.subStatusId
              // If the project is being set to Not Started, we should clear actual start and end dates
              if (draft.status === Status.NOT_STARTED) {
                draft.actualStartDate = ""
                draft.actualEndDate = ""
              }
            } else if (
              "actualStartDate" in optimisticUpdateData &&
              optimisticUpdateData.actualStartDate !== undefined
            ) {
              draft.actualStartDate = optimisticUpdateData.actualStartDate
              if (draft.status !== Status.REJECTED && draft.status !== Status.COMPLETED) {
                // If the actual start date is being cleared,
                // we should set status to Not Started and clear actual end date.
                // Otherwise, if there is an actual start date, we should set status to In Progress
                // and update custom status to the one provided in the optimistic update data or keep it unchanged if not provided
                if (optimisticUpdateData.actualStartDate === "") {
                  draft.status = Status.NOT_STARTED
                  draft.customStatus = Status.NOT_STARTED
                  draft.actualEndDate = ""
                } else {
                  draft.status = Status.IN_PROGRESS
                  draft.customStatus = optimisticUpdateData.customStatus || draft.customStatus || Status.IN_PROGRESS
                  draft.subStatusId = optimisticUpdateData.subStatusId || draft.subStatusId
                }
              }
            } else if ("actualEndDate" in optimisticUpdateData && optimisticUpdateData.actualEndDate !== undefined) {
              draft.actualEndDate = optimisticUpdateData.actualEndDate
              if (draft.status !== Status.REJECTED) {
                // If the actual end date is being cleared,
                // we should set status to In Progress
                // and if actual end date is being changed/set to a non-empty value,
                // we should set status to Completed and update custom status to the one provided
                // in the optimistic update data or keep it unchanged if not provided
                if (optimisticUpdateData.actualEndDate !== "") {
                  draft.status = Status.COMPLETED
                  draft.customStatus = optimisticUpdateData.customStatus || draft.customStatus || Status.COMPLETED
                  draft.subStatusId = optimisticUpdateData.subStatusId || draft.subStatusId
                  if (draft.actualStartDate === "") {
                    draft.actualStartDate = optimisticUpdateData.actualEndDate
                  }
                } else {
                  draft.status = Status.IN_PROGRESS
                  draft.customStatus = Status.IN_PROGRESS
                }
              }
            }
          })
        )
        try {
          const { data } = await queryFulfilled
          const state = getState()
          if (!hasPendingMutations(state, { endpointNames: "updateTaskStatus" }))
            dispatch(
              taskApi.util.invalidateTags([
                "Task",
                ...data.tasks.map(({ id }) => ({ type: "Task", id } as const)),
                { type: "Project", id: data.project.id },
                { type: "Board", id: taskId },
              ])
            )
        } catch {
          patchResult.undo()
        }
      },
    }),
    updateTaskPermissions: builder.mutation<TaskPermissionRecord[], TaskPermissionsUpdateData & { taskId: string }>({
      query: ({ taskId, membershipIds, abilitiesToAllow = [], abilitiesToRestrict = [] }) => ({
        method: "PUT",
        url: apiEndpoints.taskPermissions(taskId),
        body: { membershipIds, abilitiesToAllow, abilitiesToRestrict },
      }),
      invalidatesTags: (result, error, { taskId }) => [{ type: "TaskPermission", id: taskId }],
    }),
    updateTaskOrder: builder.mutation<UpdateOrderReturnData, TaskOrderUpdateData & { taskId: string }>({
      query: ({ taskId, ...taskData }) => ({
        method: "PATCH",
        url: apiEndpoints.taskOrder(taskId),
        body: taskData,
      }),
      transformResponse: ({ project, tasks }: UpdateOrderResponseData) => {
        return {
          project: project ? makeProjectViewModel(project) : null,
          tasks: tasks.map(makeTaskViewModel),
        }
      },
      invalidatesTags: (result) => {
        if (!result) return []
        return [
          "Task",
          ...result.tasks.map(({ id }) => ({ type: "Task", id } as const)),
          { type: "Project", id: result.project?.id },
        ]
      },
    }),
    addTaskManagers: builder.mutation<
      TaskViewModel,
      { taskId: string; managers: OptionType[]; boardId?: string; columnPosition?: number }
    >({
      query: ({ taskId, managers, boardId, columnPosition }) => ({
        method: "POST",
        url: apiEndpoints.taskManagers(taskId),
        body: { managerIds: managers.map((manager) => manager.id), boardId, columnPosition },
      }),
      transformResponse: makeTaskViewModel,
      onQueryStarted: async ({ taskId, managers }, { dispatch, queryFulfilled, getState }) => {
        const patchResult = dispatch(
          taskApi.util.updateQueryData("getTaskById", taskId, (draft) => {
            if (!draft.managers) draft.managers = []
            draft.managers.push(...managers)
            const managerIds = draft.managers?.map((manager) => manager.id) || []
            draft.participants = draft.participants.filter((p) => !managerIds.includes(p.id))
          })
        )
        try {
          await queryFulfilled
          const state = getState()
          if (
            !hasPendingMutations(state, {
              endpointNames: pendingEndpointNames,
            })
          )
            dispatch(taskApi.util.invalidateTags(["Task", { type: "Task", id: taskId }, "Chat"]))
        } catch {
          patchResult.undo()
        }
      },
    }),
    addTaskParticipants: builder.mutation<
      TaskViewModel,
      { taskId: string; participants: OptionType[]; boardId?: string; columnPosition?: number }
    >({
      query: ({ taskId, participants, boardId, columnPosition }) => ({
        method: "POST",
        url: apiEndpoints.taskParticipants(taskId),
        body: { participantIds: participants.map((participant) => participant.id), boardId, columnPosition },
      }),
      transformResponse: makeTaskViewModel,
      onQueryStarted: async ({ taskId, participants }, { dispatch, queryFulfilled, getState }) => {
        const patchResult = dispatch(
          taskApi.util.updateQueryData("getTaskById", taskId, (draft) => {
            if (!draft.participants) draft.participants = []
            draft.participants.push(...participants)
          })
        )
        try {
          await queryFulfilled
          const state = getState()
          if (!hasPendingMutations(state, { endpointNames: pendingEndpointNames }))
            dispatch(taskApi.util.invalidateTags(["Task", { type: "Task", id: taskId }]))
        } catch {
          patchResult.undo()
        }
      },
    }),
    removeTaskManagers: builder.mutation<
      RemoveTaskResourceReturnData,
      { taskId: string; managers: OptionType[]; boardId?: string }
    >({
      query: ({ taskId, managers, boardId }) => ({
        method: "DELETE",
        url: apiEndpoints.taskManagers(taskId),
        body: { managerIds: managers.map((manager) => manager.id), boardId },
      }),
      transformResponse: ({ task, board }: RemoveTaskResourceResponseData) => {
        return {
          task: makeTaskViewModel(task),
          board: board ? makeBoardViewModel(board) : null,
        }
      },
      onQueryStarted: async ({ taskId, managers }, { dispatch, queryFulfilled, getState }) => {
        const patchResult = dispatch(
          taskApi.util.updateQueryData("getTaskById", taskId, (draft) => {
            if (draft.managers) {
              draft.managers = draft.managers.filter((manager) => !managers.some((remove) => remove.id === manager.id))
            }
          })
        )
        try {
          const { data: result } = await queryFulfilled
          const state = getState()
          if (!hasPendingMutations(state, { endpointNames: pendingEndpointNames }))
            dispatch(
              taskApi.util.invalidateTags([
                "Task",
                { type: "Task", id: taskId },
                { type: "Board", id: result?.board?.id },
              ])
            )
        } catch {
          patchResult.undo()
        }
      },
      invalidatesTags: (result) => [
        "Task",
        { type: "Task", id: result?.task.id },
        { type: "Board", id: result?.board?.id },
      ],
    }),
    removeTaskParticipants: builder.mutation<
      RemoveTaskResourceReturnData,
      { taskId: string; participants: OptionType[]; boardId?: string }
    >({
      query: ({ taskId, participants, boardId }) => ({
        method: "DELETE",
        url: apiEndpoints.taskParticipants(taskId),
        body: { participantIds: participants.map((participant) => participant.id), boardId },
      }),
      transformResponse: ({ task, board }: RemoveTaskResourceResponseData) => {
        return {
          task: makeTaskViewModel(task),
          board: board ? makeBoardViewModel(board) : null,
        }
      },
      onQueryStarted: async ({ taskId, participants }, { dispatch, queryFulfilled, getState }) => {
        const patchResult = dispatch(
          taskApi.util.updateQueryData("getTaskById", taskId, (draft) => {
            if (draft.participants) {
              draft.participants = draft.participants.filter(
                (participant) => !participants.some((remove) => remove.id === participant.id)
              )
            }
          })
        )
        try {
          const { data } = await queryFulfilled
          const state = getState()
          if (!hasPendingMutations(state, { endpointNames: pendingEndpointNames }))
            dispatch(
              taskApi.util.invalidateTags([
                "Task",
                { type: "Task", id: taskId },
                { type: "Board", id: data?.board?.id },
              ])
            )
        } catch {
          patchResult.undo()
        }
      },
      invalidatesTags: (result) => [
        "Task",
        { type: "Task", id: result?.task.id },
        { type: "Board", id: result?.board?.id },
      ],
    }),
    addTaskSuppliers: builder.mutation<
      TaskViewModel,
      { taskId: string; suppliers: OptionType[]; boardId?: string; columnPosition?: number }
    >({
      query: ({ taskId, suppliers, boardId, columnPosition }) => ({
        method: "POST",
        url: apiEndpoints.taskSuppliers(taskId),
        body: { supplierIds: suppliers.map((supplier) => supplier.id), boardId, columnPosition },
      }),
      transformResponse: makeTaskViewModel,
      onQueryStarted: async ({ taskId, suppliers }, { dispatch, queryFulfilled, getState }) => {
        const patchResult = dispatch(
          taskApi.util.updateQueryData("getTaskById", taskId, (draft) => {
            if (!draft.suppliers) draft.suppliers = []
            draft.suppliers.push(...suppliers)
          })
        )
        try {
          await queryFulfilled
          const state = getState()
          if (!hasPendingMutations(state, { endpointNames: pendingEndpointNames }))
            dispatch(taskApi.util.invalidateTags(["Task", { type: "Task", id: taskId }]))
        } catch {
          patchResult.undo()
        }
      },
    }),
    removeTaskSuppliers: builder.mutation<
      RemoveTaskResourceReturnData,
      { taskId: string; suppliers: OptionType[]; boardId?: string }
    >({
      query: ({ taskId, suppliers, boardId }) => ({
        method: "DELETE",
        url: apiEndpoints.taskSuppliers(taskId),
        body: { supplierIds: suppliers.map((supplier) => supplier.id), boardId },
      }),
      transformResponse: ({ task, board }: RemoveTaskResourceResponseData) => {
        return {
          task: makeTaskViewModel(task),
          board: board ? makeBoardViewModel(board) : null,
        }
      },
      onQueryStarted: async ({ taskId, suppliers }, { dispatch, queryFulfilled, getState }) => {
        const patchResult = dispatch(
          taskApi.util.updateQueryData("getTaskById", taskId, (draft) => {
            if (draft.suppliers) {
              draft.suppliers = draft.suppliers.filter(
                (supplier) => !suppliers.some((remove) => remove.id === supplier.id)
              )
            }
          })
        )
        try {
          const { data } = await queryFulfilled
          const state = getState()
          if (!hasPendingMutations(state, { endpointNames: pendingEndpointNames }))
            dispatch(
              taskApi.util.invalidateTags([
                "Task",
                { type: "Task", id: taskId },
                { type: "Board", id: data?.board?.id },
              ])
            )
        } catch {
          patchResult.undo()
        }
      },
    }),
    addTaskWorkspaces: builder.mutation<
      TaskViewModel,
      { taskId: string; workspaces: OptionType[]; boardId?: string; columnPosition?: number }
    >({
      query: ({ taskId, workspaces, boardId, columnPosition }) => ({
        method: "POST",
        url: apiEndpoints.taskWorkspaces(taskId),
        body: { workspaceIds: workspaces.map((workspace) => workspace.id), boardId, columnPosition },
      }),
      transformResponse: makeTaskViewModel,
      onQueryStarted: async ({ taskId, workspaces }, { dispatch, queryFulfilled, getState }) => {
        const patchResult = dispatch(
          taskApi.util.updateQueryData("getTaskById", taskId, (draft) => {
            if (!draft.workspaces) draft.workspaces = []
            draft.workspaces.push(...workspaces)
          })
        )
        try {
          await queryFulfilled
          const state = getState()
          if (!hasPendingMutations(state, { endpointNames: pendingEndpointNames }))
            dispatch(taskApi.util.invalidateTags(["Task", { type: "Task", id: taskId }]))
        } catch {
          patchResult.undo()
        }
      },
    }),
    removeTaskWorkspaces: builder.mutation<
      RemoveTaskResourceReturnData,
      { taskId: string; workspaces: OptionType[]; boardId?: string; columnPosition?: number }
    >({
      query: ({ taskId, workspaces, boardId, columnPosition }) => ({
        method: "DELETE",
        url: apiEndpoints.taskWorkspaces(taskId),
        body: { workspaceIds: workspaces.map((workspace) => workspace.id), boardId, columnPosition },
      }),
      transformResponse: ({ task, board }: RemoveTaskResourceResponseData) => {
        return {
          task: makeTaskViewModel(task),
          board: board ? makeBoardViewModel(board) : null,
        }
      },
      onQueryStarted: async ({ taskId, workspaces }, { dispatch, queryFulfilled, getState }) => {
        const patchResult = dispatch(
          taskApi.util.updateQueryData("getTaskById", taskId, (draft) => {
            if (draft.workspaces) {
              draft.workspaces = draft.workspaces.filter(
                (workspace) => !workspaces.some((remove) => remove.id === workspace.id)
              )
            }
          })
        )
        try {
          const { data: result } = await queryFulfilled
          const state = getState()
          if (!hasPendingMutations(state, { endpointNames: pendingEndpointNames }))
            dispatch(
              taskApi.util.invalidateTags([
                "Task",
                { type: "Task", id: result?.task.id },
                { type: "Board", id: result?.board?.id },
              ])
            )
        } catch {
          patchResult.undo()
        }
      },
    }),
    addTaskDependency: builder.mutation<AddOrUpdateDependencyReturnData, AddTaskDependencyData & { taskId: string }>({
      query: ({ taskId, ...dependencyData }) => ({
        method: "POST",
        url: apiEndpoints.taskDependencies(taskId),
        body: dependencyData,
      }),
      transformResponse: ({ tasks, project }: AddOrUpdateDependencyResponseData) => {
        return {
          tasks: tasks.map(makeTaskViewModel),
          project: project ? makeProjectViewModel(project) : null,
        }
      },
      invalidatesTags: (result) => {
        if (!result) return []
        return [
          "Task",
          ...result.tasks.map(({ id }) => ({ type: "Task", id } as const)),
          { type: "Project", id: result.project?.id },
        ]
      },
    }),
    removeTaskDependency: builder.mutation<TaskViewModel[], RemoveTaskDependencyData & { taskId: string }>({
      query: ({ taskId, ganttAncestors }) => ({
        method: "DELETE",
        url: apiEndpoints.taskDependencies(taskId),
        body: { ganttAncestors },
      }),
      transformResponse: (tasks: TaskResponse[]) => tasks.map(makeTaskViewModel),
      invalidatesTags: (result = []) => ["Task", ...result.map(({ id }) => ({ type: "Task", id } as const))],
    }),
    updateTaskDependency: builder.mutation<
      AddOrUpdateDependencyReturnData,
      TaskDependencyUpdateData & { taskId: string }
    >({
      query: ({ taskId, ...dependencyData }) => ({
        method: "PATCH",
        url: apiEndpoints.taskDependencies(taskId),
        body: dependencyData,
      }),
      transformResponse: ({ tasks, project }: AddOrUpdateDependencyResponseData) => {
        return {
          tasks: tasks.map(makeTaskViewModel),
          project: project ? makeProjectViewModel(project) : null,
        }
      },
      invalidatesTags: (result) => {
        if (!result) return []
        return [
          "Task",
          ...result.tasks.map(({ id }) => ({ type: "Task", id } as const)),
          { type: "Project", id: result.project?.id },
        ]
      },
    }),
    archiveTask: builder.mutation<ArchiveOrUnarchiveTaskReturnData, { taskId: string; boardId?: string }>({
      query: ({ taskId, boardId }) => ({
        method: "PATCH",
        url: apiEndpoints.archiveTask(taskId),
        body: { boardId },
      }),
      transformResponse: ({ tasks, project }: ArchiveOrUnarchiveTaskResponseData) => {
        return {
          tasks: tasks.map(makeTaskViewModel),
          project: project ? makeProjectViewModel(project) : null,
        }
      },
      invalidatesTags: (result) => {
        if (!result) return []
        return [
          "Task",
          ...result.tasks.map(({ id }) => ({ type: "Task", id } as const)),
          { type: "Project", id: result.project?.id },
        ]
      },
    }),
    unarchiveTask: builder.mutation<
      ArchiveOrUnarchiveTaskReturnData,
      { taskId: string; boardId?: string; unarchiveSubtasks?: boolean }
    >({
      query: ({ taskId, boardId, unarchiveSubtasks }) => ({
        method: "PATCH",
        url: apiEndpoints.unarchiveTask(taskId),
        body: { unarchiveSubtasks: Boolean(unarchiveSubtasks), boardId },
      }),
      transformResponse: ({ tasks, project }: ArchiveOrUnarchiveTaskResponseData) => {
        return {
          tasks: tasks.map(makeTaskViewModel),
          project: project ? makeProjectViewModel(project) : null,
        }
      },
      invalidatesTags: (result) => {
        if (!result) return []
        return [
          "Task",
          ...result.tasks.map(({ id }) => ({ type: "Task", id } as const)),
          { type: "Project", id: result.project?.id },
        ]
      },
    }),
    deleteTask: builder.mutation<DeleteTaskReturnData, string>({
      query: (taskId) => ({
        method: "DELETE",
        url: apiEndpoints.taskItem(taskId),
      }),
      transformResponse: (responseData: DeleteTaskResponseData) => {
        const { updatedTasks, project, deletedTaskIds } = responseData
        return {
          updatedTasks: updatedTasks.map(makeTaskViewModel),
          project: project ? makeProjectViewModel(project) : null,
          deletedTaskIds,
        }
      },
      invalidatesTags: (result, err, taskId) => {
        if (!result) return []
        return [
          "Task",
          "CalendarEvent",
          "Todo",
          { type: "Task", id: taskId },
          { type: "Project", id: result.project?.id },
          { type: "Board", id: taskId },
          "Chat",
        ]
      },
    }),
    updateTaskCompletionPercentage: builder.mutation<
      UpdateCompletionPercentageReturnData,
      TaskCompletionPercentageUpdateData & { taskId: string }
    >({
      query: ({ taskId, ...taskData }) => ({
        method: "PUT",
        url: apiEndpoints.completionPercentage(taskId),
        body: taskData,
      }),
      transformResponse: ({ project, tasks, board }: UpdateCompletionPercentageResponseData) => {
        return {
          project: makeProjectViewModel(project),
          tasks: tasks.map(makeTaskViewModel),
          board: board ? makeBoardViewModel(board) : null,
        }
      },
      invalidatesTags: (result, err, { taskId }) => {
        if (!result) return []
        return [
          "Task",
          ...result.tasks.map(({ id }) => ({ type: "Task", id } as const)),
          { type: "Project", id: result.project.id },
          { type: "Board", id: taskId },
        ]
      },
    }),
  }),
})

export const {
  useGetTaskByIdQuery,
  useGetTasksQuery,
  useLazyGetTasksQuery,
  useGetTaskPermissionsQuery,
  useCreateUserTaskMutation,
  useCreateOrgTaskMutation,
  useCopyTaskMutation,
  useUpdateTaskInfoMutation,
  useUpdateTaskStatusDescriptionMutation,
  useUpdateTaskPlanMutation,
  useUpdateTaskDurationMutation,
  useUpdateTaskDaysLeftMutation,
  useUpdateTaskStatusMutation,
  useUpdateTaskPermissionsMutation,
  useAddTaskManagersMutation,
  useAddTaskParticipantsMutation,
  useRemoveTaskManagersMutation,
  useRemoveTaskParticipantsMutation,
  useAddTaskSuppliersMutation,
  useRemoveTaskSuppliersMutation,
  useAddTaskWorkspacesMutation,
  useRemoveTaskWorkspacesMutation,
  useUpdateTaskOrderMutation,
  useAddTaskDependencyMutation,
  useRemoveTaskDependencyMutation,
  useUpdateTaskDependencyMutation,
  useArchiveTaskMutation,
  useUnarchiveTaskMutation,
  useDeleteTaskMutation,
  useBulkCreateTasksMutation,
  useUpdateTaskCompletionPercentageMutation,
  usePrefetch,
} = taskApi

type TasksBaseQuery = { status?: Status | Status[]; archived?: "true" | "false"; links?: "true"; files?: "true" }
type OrgTasksQueryParams = { organisation: string }
type CustomerTasksQueryParams = { customer: string }
type MemberTasksQueryParams = { member: string }
type ProjectTasksQueryParams = { project: string }
type SupplierTasksQueryParams = { supplier: string }
type TaskTasksQueryParams = { task: string }
type WorkspaceTasksQueryParams = { workspace: string }
export type TasksQueryParams = TasksBaseQuery &
  (
    | {}
    | OrgTasksQueryParams
    | CustomerTasksQueryParams
    | MemberTasksQueryParams
    | ProjectTasksQueryParams
    | SupplierTasksQueryParams
    | TaskTasksQueryParams
    | WorkspaceTasksQueryParams
  )

export type NewTaskData = NewUserTaskData | NewOrgTaskData

export type NewUserTaskData = {
  title: string
  projectId: string
  order?: number
  parentTaskId?: string
}

export type NewOrgTaskData = {
  title: string
  orgId: string
  projectId: string
  order?: number
  parentTaskId?: string
  suppliers?: string[]
  workspaces?: string[]
}

export type NewBulkTaskData = {
  title: string
  projectId: string
  taskCount: number
  parentTaskId?: string
  orgId?: string
  order?: number
}

export type CopyTaskData = {
  taskId: string
  copyTree?: boolean
  order?: number
}

export type TaskInfoUpdateData = {
  title?: string
  description?: string
  customTaskNumber?: string
  ganttBarColor?: string
}

export type TaskStatusDescriptionUpdateData = {
  state?: TaskStateType
  statusDescription?: string | null
}

export type TaskPlanUpdateData = {
  plannedStartDate?: string
  plannedEndDate?: string
}

export type TaskDurationUpdateData = {
  duration: number
}

export type TaskDaysLeftUpdateData = {
  daysLeft: number
}

export type TaskStatusUpdateData = {
  status?: Status
  actualStartDate?: string
  actualEndDate?: string
  updateSubtasks?: boolean
  boardId?: string
  subStatusId?: string | null
  customStatus?: string
}

export type TaskPermissionsUpdateData = {
  membershipIds: string[]
  abilitiesToAllow?: string[]
  abilitiesToRestrict?: string[]
}

export type TaskOrderUpdateData = {
  projectId: string
  parentTaskId: string | null
  order?: number
}

export type AddTaskDependencyData = {
  ganttAncestorTaskId: string
  type: GanttDependencyType | undefined
  lagTime: number | undefined
  lagUnit: TreegridGanttUnit | undefined
}

export type GanttAncestor = {
  taskId: string
  type: GanttDependencyType
}

export type RemoveTaskDependencyData = {
  ganttAncestors: GanttAncestor[]
}

export type TaskDependencyUpdateData = {
  ganttAncestorTaskId: string
  type: "ss" | "sf" | "ff" | "fs" | undefined
  lagTime: number | undefined
  lagUnit: TreegridGanttUnit | undefined
}

export type TaskCompletionPercentageUpdateData = {
  completionPercentage: number
  boardId?: string
}

type CreateTaskResponseData = {
  created: TaskResponse
  updated: {
    tasks: TaskResponse[]
    project: ProjectResponse
  }
}

export type CreateTaskReturnData = {
  created: TaskViewModel
  updated: {
    tasks: TaskViewModel[]
    project: ProjectViewModel
  }
}

type BulkCreateTaskResponseData = {
  created: TaskViewModel[]
  updated: {
    tasks: TaskViewModel[]
    project: ProjectViewModel | null
  }
}

export type BulkCreateTasksReturnData = {
  created: TaskViewModel[]
  updated: {
    tasks: TaskViewModel[]
    project: ProjectViewModel | null
  }
}

export type CopyTaskReturnData = {
  project: ProjectViewModel
  tasks: TaskViewModel[]
}

type UpdatePlanReturnData = {
  project: ProjectViewModel
  tasks: TaskViewModel[]
}

type UpdateStatusResponseData = {
  project: ProjectResponse
  tasks: TaskResponse[]
  board: any
}

type UpdateStatusReturnData = {
  project: ProjectViewModel
  tasks: TaskViewModel[]
  board: BoardViewModel | null
}

type UpdateOrderResponseData = {
  project: ProjectResponse | null
  tasks: TaskResponse[]
}

type UpdateOrderReturnData = {
  project: ProjectViewModel | null
  tasks: TaskViewModel[]
}

type RemoveTaskResourceResponseData = {
  task: TaskResponse
  board: any
}

type RemoveTaskResourceReturnData = {
  task: TaskViewModel
  board: BoardViewModel | null
}

type AddOrUpdateDependencyReturnData = {
  tasks: TaskViewModel[]
  project: ProjectViewModel | null
}

type AddOrUpdateDependencyResponseData = {
  tasks: TaskResponse[]
  project: ProjectResponse | null
}

type ArchiveOrUnarchiveTaskReturnData = {
  tasks: TaskViewModel[]
  project: ProjectViewModel | null
}

type ArchiveOrUnarchiveTaskResponseData = {
  tasks: TaskResponse[]
  project: ProjectResponse | null
}

type DeleteTaskReturnData = {
  updatedTasks: TaskViewModel[]
  project: ProjectViewModel | null
  deletedTaskIds: string[]
}

type DeleteTaskResponseData = {
  updatedTasks: TaskResponse[]
  project: ProjectResponse | null
  deletedTaskIds: string[]
}

type UpdateCompletionPercentageResponseData = {
  project: ProjectResponse
  tasks: TaskResponse[]
  board: any
}

type UpdateCompletionPercentageReturnData = {
  project: ProjectViewModel
  tasks: TaskViewModel[]
  board: BoardViewModel | null
}
